import { connectionManager } from '../supabase/connectionManager';
import {
  MatchplayTournament,
  MatchplayMatch,
  MatchplayHole,
  MatchplayPlayer
} from '../../types/matchplay-v2';

export interface MatchplayRealtimeCallbacks {
  onTournamentUpdate?: (tournament: MatchplayTournament) => void;
  onMatchUpdate?: (match: MatchplayMatch) => void;
  onHoleUpdate?: (hole: MatchplayHole) => void;
  onPlayerUpdate?: (player: MatchplayPlayer) => void;
}

export class MatchplayRealtime {
  private static activeChannels = new Map<string, any>();
  private static subscriptions = new Map<string, MatchplayRealtimeCallbacks>();

  static subscribeToTournament(
    tournamentId: string, 
    callbacks: MatchplayRealtimeCallbacks
  ): () => void {
    const supabase = connectionManager.getClient();
    if (!supabase) {
      console.warn('Supabase client not available for real-time subscriptions');
      return () => {};
    }

    // Store callbacks
    this.subscriptions.set(tournamentId, callbacks);

    // Clean up existing subscriptions for this tournament
    this.unsubscribeFromTournament(tournamentId);

    const channels: any[] = [];

    // Subscribe to tournament changes
    if (callbacks.onTournamentUpdate) {
      const tournamentChannel = supabase
        .channel(`matchplay_tournament_${tournamentId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'matchplay_tournaments',
            filter: `id=eq.${tournamentId}`
          },
          async (payload) => {
            if (payload.new) {
              const tournament = this.mapTournamentRow(payload.new);
              callbacks.onTournamentUpdate?.(tournament);
            }
          }
        )
        .subscribe();

      channels.push(['tournament', tournamentChannel]);
    }

    // Subscribe to match changes
    if (callbacks.onMatchUpdate) {
      const matchChannel = supabase
        .channel(`matchplay_matches_${tournamentId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'matchplay_matches',
            filter: `tournament_id=eq.${tournamentId}`
          },
          async (payload) => {
            if (payload.new) {
              // Get the full match with holes
              try {
                const { MatchplayDatabase } = await import('./database');
                const match = await MatchplayDatabase.getMatch(payload.new.id);
                if (match) {
                  callbacks.onMatchUpdate?.(match);
                }
              } catch (error) {
                console.error('Error fetching updated match:', error);
              }
            }
          }
        )
        .subscribe();

      channels.push(['matches', matchChannel]);
    }

    // Subscribe to hole changes
    if (callbacks.onHoleUpdate) {
      const holeChannel = supabase
        .channel(`matchplay_holes_${tournamentId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'matchplay_holes'
          },
          async (payload) => {
            if (payload.new) {
              // Check if this hole belongs to a match in our tournament
              try {
                const { MatchplayDatabase } = await import('./database');
                const match = await MatchplayDatabase.getMatch(payload.new.match_id);
                if (match && match.tournamentId === tournamentId) {
                  const hole = this.mapHoleRow(payload.new);
                  callbacks.onHoleUpdate?.(hole);
                  
                  // Also trigger match update since holes changed
                  callbacks.onMatchUpdate?.(match);
                }
              } catch (error) {
                console.error('Error processing hole update:', error);
              }
            }
          }
        )
        .subscribe();

      channels.push(['holes', holeChannel]);
    }

    // Subscribe to player changes
    if (callbacks.onPlayerUpdate) {
      const playerChannel = supabase
        .channel(`matchplay_players_${tournamentId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'matchplay_players',
            filter: `tournament_id=eq.${tournamentId}`
          },
          async (payload) => {
            if (payload.new) {
              const player = this.mapPlayerRow(payload.new);
              callbacks.onPlayerUpdate?.(player);
            }
          }
        )
        .subscribe();

      channels.push(['players', playerChannel]);
    }

    // Store active channels
    this.activeChannels.set(tournamentId, channels);

    // Return unsubscribe function
    return () => this.unsubscribeFromTournament(tournamentId);
  }

  static unsubscribeFromTournament(tournamentId: string): void {
    const channels = this.activeChannels.get(tournamentId);
    if (channels) {
      channels.forEach(([name, channel]: [string, any]) => {
        try {
          channel.unsubscribe();
        } catch (error) {
          console.warn(`Error unsubscribing from ${name} channel:`, error);
        }
      });
      this.activeChannels.delete(tournamentId);
    }
    this.subscriptions.delete(tournamentId);
  }

  static subscribeToMatch(
    matchId: string,
    callbacks: {
      onMatchUpdate?: (match: MatchplayMatch) => void;
      onHoleUpdate?: (hole: MatchplayHole) => void;
    }
  ): () => void {
    const supabase = connectionManager.getClient();
    if (!supabase) {
      console.warn('Supabase client not available for real-time subscriptions');
      return () => {};
    }

    const channels: any[] = [];

    // Subscribe to match updates
    if (callbacks.onMatchUpdate) {
      const matchChannel = supabase
        .channel(`match_${matchId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'matchplay_matches',
            filter: `id=eq.${matchId}`
          },
          async (payload) => {
            if (payload.new) {
              try {
                const { MatchplayDatabase } = await import('./database');
                const match = await MatchplayDatabase.getMatch(matchId);
                if (match) {
                  callbacks.onMatchUpdate?.(match);
                }
              } catch (error) {
                console.error('Error fetching updated match:', error);
              }
            }
          }
        )
        .subscribe();

      channels.push(matchChannel);
    }

    // Subscribe to hole updates for this match
    if (callbacks.onHoleUpdate) {
      const holeChannel = supabase
        .channel(`match_holes_${matchId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'matchplay_holes',
            filter: `match_id=eq.${matchId}`
          },
          async (payload) => {
            if (payload.new) {
              const hole = this.mapHoleRow(payload.new);
              callbacks.onHoleUpdate?.(hole);
            }
          }
        )
        .subscribe();

      channels.push(holeChannel);
    }

    // Return unsubscribe function
    return () => {
      channels.forEach(channel => {
        try {
          channel.unsubscribe();
        } catch (error) {
          console.warn('Error unsubscribing from match channel:', error);
        }
      });
    };
  }

  static subscribeToAllMatches(
    callbacks: {
      onMatchUpdate?: (match: MatchplayMatch) => void;
      onHoleUpdate?: (hole: MatchplayHole) => void;
    }
  ): () => void {
    const supabase = connectionManager.getClient();
    if (!supabase) {
      console.warn('Supabase client not available for real-time subscriptions');
      return () => {};
    }

    const channels: any[] = [];

    // Subscribe to all match updates
    if (callbacks.onMatchUpdate) {
      const matchChannel = supabase
        .channel('all_matches')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'matchplay_matches'
          },
          async (payload) => {
            if (payload.new) {
              try {
                const { MatchplayDatabase } = await import('./database');
                const match = await MatchplayDatabase.getMatch(payload.new.id);
                if (match) {
                  callbacks.onMatchUpdate?.(match);
                }
              } catch (error) {
                console.error('Error fetching updated match:', error);
              }
            }
          }
        )
        .subscribe();

      channels.push(matchChannel);
    }

    // Subscribe to all hole updates
    if (callbacks.onHoleUpdate) {
      const holeChannel = supabase
        .channel('all_holes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'matchplay_holes'
          },
          async (payload) => {
            if (payload.new) {
              const hole = this.mapHoleRow(payload.new);
              callbacks.onHoleUpdate?.(hole);
            }
          }
        )
        .subscribe();

      channels.push(holeChannel);
    }

    // Return unsubscribe function
    return () => {
      channels.forEach(channel => {
        try {
          channel.unsubscribe();
        } catch (error) {
          console.warn('Error unsubscribing from global channel:', error);
        }
      });
    };
  }

  // Helper method to clean up all subscriptions
  static cleanup(): void {
    const tournamentIds = Array.from(this.activeChannels.keys());
    tournamentIds.forEach(id => this.unsubscribeFromTournament(id));
  }

  // Mapping functions (same as in database.ts)
  private static mapTournamentRow(row: any): MatchplayTournament {
    return {
      id: row.id,
      name: row.name,
      format: row.format,
      courseId: row.course_id,
      startDate: row.start_date,
      endDate: row.end_date,
      status: row.status,
      metadata: row.metadata || {},
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  }

  private static mapPlayerRow(row: any): MatchplayPlayer {
    return {
      id: row.id,
      tournamentId: row.tournament_id,
      playerName: row.player_name,
      handicap: row.handicap,
      courseHandicap: row.course_handicap,
      teamAssignment: row.team_assignment,
      pairingGroup: row.pairing_group,
      createdAt: row.created_at
    };
  }

  private static mapHoleRow(row: any): MatchplayHole {
    return {
      id: row.id,
      matchId: row.match_id,
      holeNumber: row.hole_number,
      team1GrossScores: row.team1_gross_scores,
      team2GrossScores: row.team2_gross_scores,
      team1NetScores: row.team1_net_scores,
      team2NetScores: row.team2_net_scores,
      team1PlayingScore: row.team1_playing_score,
      team2PlayingScore: row.team2_playing_score,
      holeWinner: row.hole_winner,
      strokesApplied: row.strokes_applied || {},
      createdAt: row.created_at
    };
  }
} 